/**
 * Typed API client — TASK 7.3 Step 3 / 3b.
 *
 * The single network path out of the PWA. Everything it guarantees:
 *
 *   - `credentials: 'same-origin'` on every request (the session is an
 *     HttpOnly first-party cookie; the client never sees or stores a token);
 *   - the `{ code, message, fieldErrors? }` envelope from
 *     `@streamymusic/shared` parsed into a typed {@link ApiError} — with a
 *     malformed / non-JSON / HTML body collapsing to a SAFE generic message
 *     instead of throwing a parse error or echoing attacker-controlled bytes
 *     into the UI (see {@link parseErrorBody});
 *   - the CSRF double-submit header (`x-csrf-token`) on every state-changing
 *     method, minted first via `GET /api/me` when the cookie is absent;
 *   - `Idempotency-Key` (CSPRNG `crypto.randomUUID()`, never `Math.random`) on
 *     add-type mutations;
 *   - a latest-request-wins guard for search (AC 11.8);
 *   - the global 403 handler (AC 2.5) — with the revocation/CSRF/role
 *     DISCRIMINATION described below.
 *
 * ── THE 403 PROBLEM (read this before touching `classifyForbidden`) ───────
 * `apps/api` returns **the identical envelope** `{code:'FORBIDDEN'}` for three
 * completely different conditions:
 *
 *   (a) allowlist removal / revocation  — `plugins/authz.ts` gate
 *   (b) role denial (member → admin route) — `requireAdmin()`
 *   (c) CSRF double-submit failure      — `plugins/csrf.ts` onRequest hook
 *
 * Only (a) may trigger the purge. A blanket "any 403 → wipe local data + sign
 * out" would DESTROY a legitimate user's downloaded audio the moment they
 * tapped an admin link, or when a CSRF cookie went stale — data loss, not
 * security. Since the wire cannot distinguish them today, this client asks the
 * one endpoint whose 403 is unambiguous:
 *
 *   **`GET /api/me`** is a GET (so the CSRF hook cannot reject it — (c) is
 *   impossible) on a non-admin route (so `requireAdmin` never runs — (b) is
 *   impossible). Its status is therefore a clean read of the allowlist gate:
 *
 *     403 → (a) revoked   → purge + `#/signin/denied`
 *     401 → session gone  → `#/signin`, **no purge** (an expired cookie is not
 *                           a revocation; wiping downloads would be the same
 *                           data-loss bug in a different coat)
 *     200 → NOT revoked   → (b) or (c). No purge. If the failing request was a
 *                           mutation and the probe returned a DIFFERENT csrf
 *                           token, the stale-token case (c) is retried once.
 *
 *   A 403 on `/api/me` itself skips the probe (it is already authoritative).
 *
 * FOLLOW-UP (server, not shippable from here): giving the three conditions
 * distinct `code`s would let the probe be deleted. It requires adding codes to
 * the shared `ERROR_CODES` enum, which is outside this task's file set — see
 * the task report. The probe is correct without it, at the cost of one extra
 * GET per 403.
 *
 * ── NO LOGGING OF SECRETS ─────────────────────────────────────────────────
 * Cookies, the CSRF token, idempotency keys, request bodies and response
 * bodies are never logged, at any level. Diagnostics carry method + path +
 * status + error code only.
 *
 * @module
 */

import {
  ERROR_CODES,
  PLAY_EVENT_BATCH_MAX,
  SAFE_ERROR_MESSAGES,
  adminUserListSchema,
  albumDetailResponseSchema,
  artistAlbumsResponseSchema,
  artistListResponseSchema,
  albumListResponseSchema,
  errorEnvelopeSchema,
  favoriteResponseSchema,
  favoritesResponseSchema,
  genreAlbumsResponseSchema,
  genreListResponseSchema,
  homeResponseSchema,
  lyricsResponseSchema,
  meResponseSchema,
  playEventIngestResponseSchema,
  playlistDetailResponseSchema,
  playlistListResponseSchema,
  playlistSummarySchema,
  queueHistoryEntrySchema,
  queueHistoryResponseSchema,
  queueMutationResponseSchema,
  queueReconcileResponseSchema,
  queueResponseSchema,
  recentlyPlayedTracksResponseSchema,
  searchResponseSchema,
  statsResponseSchema,
  syncStatusResponseSchema,
  syncTriggerResponseSchema,
  type AddFavoriteRequest,
  type AddPlaylistTracksRequest,
  type AdminUserList,
  type AlbumDetailResponse,
  type ArtistAlbumsResponse,
  type ArtistListResponse,
  type AlbumListResponse,
  type ErrorCode,
  type FavoriteResponse,
  type FavoriteTargetType,
  type FavoritesResponse,
  type GenreAlbumsResponse,
  type GenreListResponse,
  type HomeResponse,
  type LyricsResponse,
  type MoveDirective,
  type PlayEventInput,
  type PlayEventIngestResponse,
  type PlaylistDetailResponse,
  type PlaylistListResponse,
  type PlaylistSummary,
  type QueueHistoryEntry,
  type QueueHistoryResponse,
  type QueueMutationResponse,
  type QueueOp,
  type QueueReconcileResponse,
  type QueueResponse,
  type RecentlyPlayedTracksResponse,
  type RecordQueueHistoryRequest,
  type Role,
  type SearchGroupName,
  type SearchResponse,
  type StatsPeriod,
  type StatsResponse,
  type SyncStatusResponse,
  type SyncTriggerResponse,
} from '@streamymusic/shared';

import { navigate } from '../modules/app/router.js';
import { purgeLocalState } from '../lib/local-purge.js';
import { createTtlCache, type TtlCache } from '../lib/ttl-cache.js';

/**
 * Re-exported so a view can build the values these methods take —
 * `{ kind: 'to', position: n }` for {@link ApiClient.reorderPlaylist} /
 * {@link ApiClient.mutateQueue}, and a group name for
 * {@link SearchOptions.type} — without a second import path.
 */
export type { MoveDirective, QueueOp, SearchGroupName } from '@streamymusic/shared';

/** The CSRF cookie the server issues (deliberately NOT HttpOnly). */
export const CSRF_COOKIE_NAME = 'csrf_token';
/** The header the double-submit value is echoed in (`plugins/csrf.ts`). */
export const CSRF_HEADER_NAME = 'x-csrf-token';
/** Idempotency header for add-type mutations (§8 API conventions). */
export const IDEMPOTENCY_HEADER_NAME = 'idempotency-key';

const STATE_CHANGING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/** Status → code fallback for a response whose body is not a valid envelope. */
const STATUS_TO_CODE: ReadonlyMap<number, ErrorCode> = new Map<number, ErrorCode>([
  [401, ERROR_CODES.UNAUTHENTICATED],
  [403, ERROR_CODES.FORBIDDEN],
  [404, ERROR_CODES.NOT_FOUND],
  [409, ERROR_CODES.CONFLICT],
  [410, ERROR_CODES.GONE],
  [413, ERROR_CODES.PAYLOAD_TOO_LARGE],
  [422, ERROR_CODES.VALIDATION],
  [429, ERROR_CODES.RATE_LIMITED],
]);

/**
 * A failed API call, carrying the machine-readable `code` UI surfaces branch
 * on. `message` is ALWAYS one of the safe strings — never raw server text.
 */
export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly fieldErrors: Readonly<Record<string, string>> | undefined;

  constructor(code: ErrorCode, status: number, fieldErrors?: Readonly<Record<string, string>>) {
    super(SAFE_ERROR_MESSAGES[code]);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.fieldErrors = fieldErrors;
  }
}

/** Raised when the transport itself failed (offline, DNS, aborted). */
export class NetworkError extends Error {
  /** True when an `AbortController` cancelled the request. */
  readonly aborted: boolean;
  constructor(aborted: boolean) {
    super(aborted ? 'The request was cancelled.' : 'The network is unavailable.');
    this.name = 'NetworkError';
    this.aborted = aborted;
  }
}

/* ── Envelope parsing ──────────────────────────────────────────────────── */

/**
 * Turns ANY error response into a typed {@link ApiError}.
 *
 * A non-JSON body (an HTML 502 page, a proxy's plain-text error, a truncated
 * stream) must not throw and must not reach the UI: it collapses to the
 * status's code with the fixed safe message. This is the "never surface raw
 * server text" rule — a reflected-XSS / phishing vector otherwise.
 */
export function parseErrorBody(status: number, raw: unknown): ApiError {
  const fallback = STATUS_TO_CODE.get(status) ?? ERROR_CODES.INTERNAL;
  const parsed = errorEnvelopeSchema.safeParse(raw);
  if (!parsed.success) return new ApiError(fallback, status);
  const { code, fieldErrors } = parsed.data;
  return new ApiError(code, status, fieldErrors);
}

/**
 * The actionable text inside a 422, or `null` when there is none.
 *
 * EVERY CAP IN THE USER-STATE FAMILY IS A `VALIDATION` ERROR with no dedicated
 * code: the 5,000-tracks-per-playlist cap, the 500-playlists-per-user cap, the
 * 1,000-item queue cap and a duplicate playlist name all arrive as HTTP 422
 * `{code:'VALIDATION', fieldErrors:{...}}`, distinguishable ONLY by the
 * `fieldErrors` entry and its human-readable message. `error.message` on those
 * is the generic "Some fields are invalid." — rendering it for a full playlist
 * tells the user nothing. Views should therefore prefer
 * `fieldErrorText(error) ?? error.message` over `error.message` alone for any
 * playlist/queue/favorites mutation failure.
 *
 * Total by design: any non-{@link ApiError} input (a `NetworkError`, a plain
 * throw, `undefined`) and an empty/absent `fieldErrors` all yield `null`.
 * Never throws.
 */
export function fieldErrorText(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  if (error.fieldErrors === undefined) return null;
  for (const text of Object.values(error.fieldErrors)) {
    if (text !== '') return text;
  }
  return null;
}

/** `JSON.parse` that yields `undefined` instead of throwing. */
async function readJson(response: Response): Promise<unknown> {
  let text: string;
  try {
    text = await response.text();
  } catch {
    return undefined;
  }
  if (text === '') return undefined;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return undefined;
  }
}

/* ── Cookies ───────────────────────────────────────────────────────────── */

/**
 * Reads one cookie from a `document.cookie`-shaped string. The CSRF cookie is
 * intentionally readable by first-party JS — that IS the double-submit
 * pattern (`apps/api/src/plugins/csrf.ts` header).
 */
export function readCookieFrom(cookieString: string, name: string): string | null {
  for (const part of cookieString.split(';')) {
    const eq = part.indexOf('=');
    if (eq === -1) continue;
    if (part.slice(0, eq).trim() !== name) continue;
    const value = part.slice(eq + 1).trim();
    return value === '' ? null : value;
  }
  return null;
}

/* ── Latest-request-wins (AC 11.8) ─────────────────────────────────────── */

export type LatestWinsOutcome<T> =
  { readonly stale: true } | { readonly stale: false; readonly value: T };

export interface LatestWins<T> {
  /**
   * Runs `task`, aborting whatever was in flight. The returned outcome is
   * `{stale:true}` when a NEWER call started before this one settled — the
   * caller must then write nothing, so an out-of-order response can never
   * overwrite a fresher one.
   */
  run(task: (signal: AbortSignal) => Promise<T>): Promise<LatestWinsOutcome<T>>;
  /** Cancels the in-flight request (e.g. the search box was cleared). */
  abort(): void;
}

/** Creates a guard whose results are only ever the newest issued request's. */
export function createLatestWins<T>(): LatestWins<T> {
  let sequence = 0;
  let controller: AbortController | null = null;

  return {
    async run(task) {
      controller?.abort();
      const own = (sequence += 1);
      const ownController = new AbortController();
      controller = ownController;
      try {
        const value = await task(ownController.signal);
        // The ONLY gate that matters: a stale settle is discarded even if the
        // abort did not (or could not) stop the older fetch in time.
        if (own !== sequence) return { stale: true };
        return { stale: false, value };
      } catch (error) {
        if (own !== sequence) return { stale: true };
        throw error;
      } finally {
        if (controller === ownController) controller = null;
      }
    },
    abort() {
      controller?.abort();
      controller = null;
      sequence += 1;
    },
  };
}

/* ── Client ────────────────────────────────────────────────────────────── */

/** Outcome of the session probe. `unknown` = the check could not complete. */
export type SessionResult =
  | {
      readonly status: 'authenticated';
      readonly role: Role;
      readonly csrfToken: string;
      /**
       * `GET /api/me`'s `userId`. Carried so the composition root can stamp
       * locally-parked PlayEvents with the session that captured them — see
       * `lib/offline-store.ts#PLAY_EVENT_OWNER_FIELD` (Security W4). It is not
       * a display value; nothing in the UI renders it.
       */
      readonly userId: string;
    }
  | { readonly status: 'unauthenticated' }
  | { readonly status: 'unknown' };

export interface RequestOptions<T> {
  readonly method?: string;
  readonly body?: unknown;
  readonly query?: Readonly<Record<string, string | number | boolean>>;
  readonly signal?: AbortSignal;
  /** Attach an `Idempotency-Key` (add-type mutations, §8). */
  readonly idempotent?: boolean;
  /** Runtime validator; a validation failure becomes a safe INTERNAL error. */
  readonly parse?: (data: unknown) => T;
  /** Opt out of the global 401/403 handling (used by the probe + `getSession`). */
  readonly raw?: boolean;
  /**
   * Survive page teardown (`pagehide`/`visibilitychange`). Set only by the
   * audio engine's end-of-session play-event flush; see
   * {@link ApiClient.sendPlayEvents}.
   */
  readonly keepalive?: boolean;
}

export interface ApiClientDeps {
  readonly fetchImpl?: typeof fetch;
  /** Source of `document.cookie`. */
  readonly readCookie?: (name: string) => string | null;
  /** Hash navigation; defaults to `router.js#navigate`. */
  readonly go?: (hash: string) => void;
  /** The shared erase routine; defaults to `local-purge.js#purgeLocalState`. */
  readonly purge?: () => Promise<unknown>;
  /** CSPRNG idempotency keys; defaults to `crypto.randomUUID()`. */
  readonly newIdempotencyKey?: () => string;
  readonly baseUrl?: string;
  /**
   * TTL cache for the STATIC catalog browse responses — the artist and genre
   * lists and grids (feature #4). Defaults to a 24h `localStorage`-backed cache;
   * pass a `null`-storage cache to disable persistence, or your own for tests.
   */
  readonly catalogCache?: TtlCache;
}

/** Cursor-paged read options shared by the catalog browse endpoints. */
export interface PageOptions {
  readonly limit?: number;
  /** Echo `nextCursor` back verbatim; `null`/absent asks for the first page. */
  readonly cursor?: string | null;
  readonly signal?: AbortSignal;
}

/**
 * Options for {@link ApiClient.search}. No `signal` here on purpose: the
 * latest-wins guard owns the request's lifetime (AC 11.8), and a caller-held
 * signal would fight it.
 */
export interface SearchOptions {
  /** Per-group page size; the server clamps to <= 200. */
  readonly limit?: number;
  /**
   * A group's `nextCursor`, echoed back verbatim; `null`/absent = first page.
   * REQUIRES {@link SearchOptions.type} — pagination is independent PER GROUP,
   * so a cursor without the group it belongs to is rejected 422
   * (`searchQuerySchema`'s refine).
   */
  readonly cursor?: string | null;
  /** Narrow the response to one group. Mandatory whenever `cursor` is sent. */
  readonly type?: SearchGroupName;
  /** Genre filter chip (AC 11.9). An unknown id is `NOT_FOUND`. */
  readonly genreId?: string;
}

/**
 * Options for {@link ApiClient.listFavorites}. Same per-section cursor rule as
 * search: a `cursor` without `type` is rejected 422 (`favoritesQuerySchema`'s
 * refine), because each section pages independently.
 */
export interface FavoritesPageOptions extends PageOptions {
  /** Narrow to one section; REQUIRED whenever `cursor` is sent. */
  readonly type?: FavoriteTargetType;
}

/** The `{ outcome: 'ok' }` body every admin-user MUTATION route returns. */
export interface AdminUserMutationResult {
  readonly outcome: 'ok';
}

/** `POST /api/admin/users` — add/reactivate by email (routes/admin-users.ts). */
export interface AddAdminUserResult {
  readonly outcome: 'created' | 'already_invited' | 'reactivated';
}

/** Paged read options for {@link ApiClient.listAdminUsers} (offset, not cursor). */
export interface AdminUsersPageOptions {
  readonly limit?: number;
  readonly offset?: number;
  readonly signal?: AbortSignal;
}

/**
 * ── ERROR CONTRACTS ───────────────────────────────────────────────────────
 *
 * Every method below rejects with {@link ApiError} (carrying a machine-readable
 * {@link ApiError.code}) or {@link NetworkError}. The server routes document
 * exactly which statuses each endpoint can produce; the client used to mirror
 * none of it, so a caller had to read `apps/api` to know what to branch on.
 * Each method now names the codes ITS CALLER must handle — the ones with a
 * distinct UI outcome. `UNAUTHENTICATED` (401) and the revocation half of
 * `FORBIDDEN` (403) are handled GLOBALLY and never need a per-caller branch:
 * they navigate on their own before the rejection reaches you.
 *
 * Branch on `error.code`, NOT on `error.status`. Several statuses map to more
 * than one condition, and `code` is the field the server treats as the
 * contract.
 */
export interface ApiClient {
  request<T>(path: string, options?: RequestOptions<T>): Promise<T>;
  /** Never rejects: transport failure and 5xx both collapse to `unknown`. */
  getSession(): Promise<SessionResult>;
  /** Caller handles: nothing specific — any failure is "the rails are empty". */
  getHome(): Promise<HomeResponse>;
  /**
   * `GET /api/recently-played-tracks` — the caller's last 20 distinct
   * qualifyingly-played TRACKS, most recent first (dto.ts
   * `recentlyPlayedTracksResponseSchema`). Takes no parameters at all; like
   * home/stats it is scoped to the authenticated session only.
   *
   * Caller handles: nothing specific — any failure is "this rail shows its
   * own error + Retry" (the home per-section isolation rule, AC 14.3). An
   * empty `items` is the genuine "no qualifying plays yet" state, never an
   * error.
   */
  getRecentlyPlayedTracks(): Promise<RecentlyPlayedTracksResponse>;
  /**
   * `GET /api/search` — grouped, ranked search (TASK 4.2 / AC 11.x).
   *
   * With no `options.type` the response carries the first page of EVERY group;
   * with it, that one group only — which is what makes per-group "see all"
   * pagination possible. A `cursor` is only meaningful inside one group, so
   * sending it WITHOUT `type` is a 422 (see {@link SearchOptions.cursor}).
   * While `genreId` is active the `genres` group is ABSENT by design (§10.6).
   *
   * Caller handles: `VALIDATION` (query too long/short, or a cursor the server
   * will not accept — including cursor-without-type; treat as "start over",
   * never as retriable) and `NOT_FOUND` (unknown `genreId` — drop the chip).
   * Stale results are reported as `{stale:true}` rather than thrown.
   *
   * ONE GUARD, ONE SEARCH SURFACE. Every call shares a single latest-wins
   * guard, so any newer `search()` call aborts the in-flight one — exactly
   * right for the app's single search box, including its "load more of one
   * group" requests (a page fetch racing a fresh keystroke SHOULD lose). Do
   * NOT run two concurrent search UIs through this method: they would cancel
   * each other. If a second surface ever exists, give it its own
   * {@link createLatestWins} over `request()` instead of widening this.
   */
  search(query: string, options?: SearchOptions): Promise<LatestWinsOutcome<SearchResponse>>;
  /**
   * `GET /api/artists` — A-Z with album counts (TASK 8.1).
   *
   * Caller handles: `VALIDATION` (a cursor the server will not accept — treat
   * as "start over", never as a retriable transient).
   */
  getArtists(options?: PageOptions): Promise<ArtistListResponse>;

  /**
   * `GET /api/albums` — the WHOLE catalog as album tiles, for the browse
   * ALBUMS tab. Ordered artist-then-year, cursor-paged like the other grids.
   *
   * Caller handles: `VALIDATION` (a tampered cursor — treat as "start over").
   */
  getAlbums(options?: PageOptions): Promise<AlbumListResponse>;
  /**
   * `GET /api/artists/:id` — the artist's albums, year asc, no-year LAST.
   *
   * Caller handles: `NOT_FOUND` (no such artist — a path back, NO Retry) and
   * `VALIDATION` (bad cursor).
   */
  getArtistAlbums(artistId: string, options?: PageOptions): Promise<ArtistAlbumsResponse>;
  /**
   * `GET /api/albums/:id` — header + tracks disc-then-track.
   *
   * Caller handles: `NOT_FOUND` (never existed / removed from the catalog —
   * no Retry) and `GONE` (indexed but no longer in Drive — also NO Retry; a
   * Retry offered here can never succeed).
   */
  getAlbum(
    albumId: string,
    options?: { readonly signal?: AbortSignal },
  ): Promise<AlbumDetailResponse>;
  /**
   * `GET /api/tracks/:id/lyrics` — the lyrics resolution for ONE track (synced
   * LRC cues, plain unsynced text, instrumental, or none). Mirrors
   * {@link ApiClient.getHome}: a plain read, NO auto-retry.
   *
   * The response is a single shape with a `status` discriminator and two
   * always-present nullable payload fields (`lines` non-null IFF
   * `status === 'synced'`; `plain` non-null IFF `status === 'plain'`) — the
   * caller switches on `status` and never has to guess which field is set.
   *
   * Caller handles: `NOT_FOUND` (the `:id` names no track — distinct from the
   * `'none'` STATUS, which is a 200 meaning "the track exists but has no
   * lyrics"; only a truly-missing track is the 404). A transport failure is
   * the full player's inline "couldn't load lyrics" state — it never blanks the
   * player or auto-retries.
   */
  getTrackLyrics(
    trackId: string,
    options?: { readonly signal?: AbortSignal },
  ): Promise<LyricsResponse>;
  /**
   * `POST /api/favorites` — idempotent "ensure favorited".
   *
   * Caller handles: `NOT_FOUND` (the target vanished between render and tap —
   * roll the optimistic heart back) and `VALIDATION`. A replay of the same
   * `idempotencyKey` for the same target is absorbed server-side and returns a
   * byte-identical body, so a retry is always safe.
   */
  addFavorite(input: AddFavoriteRequest): Promise<FavoriteResponse>;
  /**
   * `DELETE /api/favorites/:type/:id` — 204, or 404 when already absent.
   *
   * Caller handles: `NOT_FOUND` — which is SUCCESS for a remove. The resource
   * is already absent for this caller (routes/favorites.ts documents 404 as
   * "unknown, already-removed, or another user's"), so rolling the heart back
   * would re-assert a favorite that does not exist.
   */
  removeFavorite(type: FavoriteTargetType, targetId: string): Promise<void>;
  /**
   * `GET /api/favorites` — Songs and/or Albums, each independently paged,
   * newest first (TASK 6.1 / AC 10.4).
   *
   * ABSENT ≠ EMPTY. Each response section (`tracks` / `albums`) is optional: a
   * `type`-narrowed request returns ONLY that section, and the other is ABSENT
   * — meaning "not asked for", never "the user has no favorites". Render
   * "no favorites" only for a PRESENT section with zero items; rendering it
   * for an absent one shows an empty state for data that was simply excluded.
   *
   * Entries keep `available: false` tracks/albums (favorites are not a browse
   * surface — AC 3.9); render the flag, do not filter.
   *
   * Caller handles: `VALIDATION` (bad `type`, tampered cursor, or a cursor
   * sent without `type` — see {@link FavoritesPageOptions}; treat as "start
   * over").
   */
  listFavorites(options?: FavoritesPageOptions): Promise<FavoritesResponse>;
  /**
   * `GET /api/playlists` — the caller's playlists, cursor-paged at <= 200.
   *
   * Caller handles: `VALIDATION` (tampered cursor — treat as "start over").
   */
  listPlaylists(options?: PageOptions): Promise<PlaylistListResponse>;
  /**
   * `GET /api/playlists/:id` — header + ONE cursor page of tracks (<= 200).
   * `trackCount` is the authoritative total across all pages; rows are
   * addressed by `itemId` (the same track may appear twice), and `position` is
   * the server-maintained contiguous 0-based index.
   *
   * Caller handles: `NOT_FOUND` (unknown or another user's playlist — a path
   * back, no Retry) and `VALIDATION` (bad cursor).
   */
  getPlaylist(playlistId: string, options?: PageOptions): Promise<PlaylistDetailResponse>;
  /**
   * `POST /api/playlists` — create; 201 with the new summary (AC 9.1).
   *
   * Caller handles: `VALIDATION` — blank/over-long name, DUPLICATE name, and
   * the 500-playlist cap all arrive as 422 keyed on `fieldErrors.name` with no
   * distinct code, so surface {@link fieldErrorText} rather than
   * `error.message`.
   */
  createPlaylist(name: string): Promise<PlaylistSummary>;
  /**
   * `POST /api/playlists/from-queue` — save the current queue as a playlist,
   * in queue order (AC 8.1); 201 with the new summary.
   *
   * Caller handles: `VALIDATION` — the same `fieldErrors.name` cases as
   * {@link ApiClient.createPlaylist}, PLUS an empty queue (AC 8.4 disables the
   * action client-side; the server refuses it too). Use {@link fieldErrorText}.
   */
  createPlaylistFromQueue(name: string): Promise<PlaylistSummary>;
  /**
   * `PATCH /api/playlists/:id` — rename, the only mutable playlist field.
   *
   * Caller handles: `NOT_FOUND` (unknown/foreign playlist) and `VALIDATION`
   * (name rules or a name the caller already uses — `fieldErrors.name`, so
   * prefer {@link fieldErrorText}).
   */
  renamePlaylist(playlistId: string, name: string): Promise<PlaylistSummary>;
  /**
   * `DELETE /api/playlists/:id` — 204, tracks cascade with it.
   *
   * Caller handles: `NOT_FOUND` — which is SUCCESS for a delete, exactly as
   * {@link ApiClient.removeFavorite}: the Story-6 DELETE contract
   * (routes/playlists.ts) documents 404 as "unknown, already-deleted, or
   * another user's", i.e. the playlist is absent, which is what was asked for.
   * Do not surface it as an error or retry it.
   */
  deletePlaylist(playlistId: string): Promise<void>;
  /**
   * `POST /api/playlists/:id/tracks` — append one track, or a WHOLE album in
   * album order, in one call (AC 9.2). Returns the playlist's STATE, so an
   * idempotent retry is byte-identical.
   *
   * "ADD ANYWAY" IS A FRESH KEY, NOT A FLAG. There is no `allowDuplicate`
   * field anywhere in this contract: the server appends whatever it is asked
   * to (the same track may legitimately appear twice), and duplicate-confirm
   * is purely a client dialog. When the user confirms "add anyway", repeat the
   * call with a NEW `input.idempotencyKey` (or let this method mint one by
   * omitting it) — replaying the SAME key is absorbed as a retry and adds
   * nothing.
   *
   * Caller handles: `NOT_FOUND` (playlist, or a `trackId`/`albumId` naming
   * nothing), `GONE` (the target exists but is no longer available — add-time
   * only, no Retry) and `VALIDATION` (the 5,000-track cap or an album that
   * expands to zero available tracks — keyed `fieldErrors`, use
   * {@link fieldErrorText}).
   */
  addPlaylistTracks(playlistId: string, input: AddPlaylistTracksRequest): Promise<PlaylistSummary>;
  /**
   * `DELETE /api/playlists/:id/tracks/:itemId` — 204 WITH NO BODY: removing a
   * row shifts every later `position`, so there is no summary to trust and the
   * caller must re-fetch `getPlaylist` to re-render (the fresh read carries
   * the same `updatedAt` LWW token a body would have).
   *
   * Caller handles: `NOT_FOUND` — SUCCESS, per the same DELETE contract as
   * {@link ApiClient.deletePlaylist}: the row is already absent for this
   * caller.
   */
  removePlaylistTrack(playlistId: string, itemId: string): Promise<void>;
  /**
   * `POST /api/playlists/:id/reorder` — up / down / top / bottom /
   * `{kind:'to', position:n}` (AC 9.3, the accessible non-gesture path). A
   * move that resolves to where the item already is (`up` at the top, `to`
   * beyond the end — the server CLAMPS) is a 200 no-op, never an error.
   *
   * Caller handles: `NOT_FOUND` (playlist, or an `itemId` not a row of it —
   * refresh the detail page) and `VALIDATION` (malformed directive).
   */
  reorderPlaylist(
    playlistId: string,
    itemId: string,
    move: MoveDirective,
  ): Promise<PlaylistSummary>;
  /**
   * `GET /api/queue` — the DISPLAY read: one OPAQUE-cursor page of <= 200
   * items for rendering "up next" (TASK 6.3 / AC 7.x).
   *
   * NEVER REBUILD LOCAL STATE FROM THESE PAGES. A mutation landing between two
   * pages would let a client assemble an order that never existed on any
   * device; the route file is explicit that reconstruction belongs to
   * {@link ApiClient.reconcileQueue}, whose per-chunk `version` makes exactly
   * that interleaving detectable. This method's cursors and reconcile's
   * numeric `after` are NOT interchangeable.
   *
   * Caller handles: `VALIDATION` (tampered cursor — treat as "start over").
   */
  getQueue(options?: PageOptions): Promise<QueueResponse>;
  /**
   * `GET /api/queue/reconcile` — one bounded chunk (<= 500) of the FULL
   * ordered queue, for rebuilding local state (AC 7.5). `after` is a NUMERIC
   * POSITION, not an opaque cursor — do not feed it a `nextCursor` from
   * {@link ApiClient.getQueue} or vice versa.
   *
   * The walk: start with no `after`, keep sending `nextAfter` until it is
   * `null`, then adopt the assembly verbatim (server wins). If `version`
   * CHANGES between chunks, discard the partial assembly and restart — a
   * mutation interleaved mid-walk. Keep the final `version` for
   * {@link ApiClient.mutateQueue}.
   *
   * Caller handles: `VALIDATION` (`after` negative or beyond the 1,000-item
   * cap — restart the walk from the top).
   */
  reconcileQueue(options?: {
    readonly after?: number;
    readonly limit?: number;
    readonly signal?: AbortSignal;
  }): Promise<QueueReconcileResponse>;
  /**
   * `PUT /api/queue` — apply one queue op; returns the new STATE (version +
   * counts, no items — re-read those via the bounded reads).
   *
   * ALWAYS SEND THE VERSION YOU HOLD. Omitting `options.version` applies the
   * op UNCONDITIONALLY — a silent clobber of whatever another device wrote —
   * and is only legitimate on a first-ever write or immediately after a full
   * reconcile. A stale version answers `CONFLICT` (409): another device wrote
   * first, so the caller must re-run {@link ApiClient.reconcileQueue} and
   * re-apply on top — never strip the version and try again.
   *
   * `add`/`playNext` ops are add-type mutations: this method mints a CSPRNG
   * `idempotencyKey` into the op body when the caller did not supply one, so a
   * retried PUT is absorbed instead of double-appending.
   *
   * Caller handles: `CONFLICT` (stale version — reconcile, do not clobber),
   * `NOT_FOUND` (a `reorder`/`remove` itemId that is not the caller's, or an
   * add trackId naming nothing), `GONE` (an add track that is no longer
   * available) and `VALIDATION` (the 1,000-item cap or `setCurrentIndex` past
   * the end — keyed `fieldErrors`, use {@link fieldErrorText}).
   */
  mutateQueue(op: QueueOp, options?: { readonly version?: string }): Promise<QueueMutationResponse>;
  /**
   * `GET /api/queue/history` — the last 100 finished/skipped tracks, newest
   * first, OPAQUE-cursor paged (AC 7.3). Not interchangeable with reconcile's
   * numeric positions.
   *
   * Caller handles: `VALIDATION` (tampered cursor — treat as "start over").
   */
  getQueueHistory(options?: PageOptions): Promise<QueueHistoryResponse>;
  /**
   * `POST /api/queue/history` — record "this track finished/skipped"; 201 with
   * the entry. Fires the moment a track ends, which makes it the most-retried
   * request in the app, so this method mints an `idempotencyKey` (caller-
   * overridable via `input.idempotencyKey`, e.g. for outbox replay): a replay
   * returns the ORIGINAL entry instead of double-writing and evicting a real
   * row from the retained hundred.
   *
   * Caller handles: `NOT_FOUND` (the `trackId` names no track) and
   * `VALIDATION`. An UNAVAILABLE track is accepted deliberately — the play
   * already happened.
   */
  recordQueueHistory(input: RecordQueueHistoryRequest): Promise<QueueHistoryEntry>;
  /**
   * `POST /api/queue/history/:id/readd` — append that entry's track back onto
   * the queue (the track id comes from the ROW, never from the caller). An
   * `idempotencyKey` is minted when not supplied, exactly as
   * {@link ApiClient.recordQueueHistory}.
   *
   * Caller handles: `NOT_FOUND` (unknown or another user's entry — refresh
   * the history list), `GONE` (the track has since become unavailable — no
   * Retry) and `VALIDATION` (the 1,000-item queue cap — keyed `fieldErrors`,
   * use {@link fieldErrorText}).
   */
  readdQueueHistory(
    historyId: string,
    options?: { readonly idempotencyKey?: string },
  ): Promise<QueueMutationResponse>;
  /**
   * `POST /api/play-events` — the COUNTED playback events (TASK 8.2).
   *
   * Batches larger than `PLAY_EVENT_BATCH_MAX` are SPLIT here rather than sent
   * and rejected: `errors.ts` makes an oversized batch a 413, and a 413 that
   * reaches an offline client which has accumulated a week of events is a
   * whole queue silently lost. The returned counts are the sum across chunks.
   *
   * Caller handles: `VALIDATION` (422 — one malformed event fails the WHOLE
   * batch, so callers must not send unvalidated input) and `PAYLOAD_TOO_LARGE`.
   * Callers are diagnostics-only and must swallow everything: a telemetry
   * failure may never break playback.
   *
   * @param options.keepalive deliver with `fetch(..., {keepalive:true})` so the
   *   request survives page teardown. Used by the engine's `pagehide` flush —
   *   `navigator.sendBeacon` cannot be used for this, because it sets no
   *   headers and `plugins/csrf.ts` rejects every header-less POST.
   */
  sendPlayEvents(
    events: readonly PlayEventInput[],
    options?: { readonly keepalive?: boolean },
  ): Promise<PlayEventIngestResponse>;
  /**
   * `GET /api/stats?period=` — Top 25 songs + Top 25 albums for one period
   * (TASK 4.4). Mirrors {@link ApiClient.getHome}: no auto-retry, the query is
   * OMITTED entirely when `period` is absent (the server defaults to all_time).
   *
   * Caller handles: nothing specific — a failure is "the numbers are empty".
   */
  getStats(options?: {
    readonly period?: StatsPeriod;
    readonly signal?: AbortSignal;
  }): Promise<StatsResponse>;
  /**
   * `GET /api/genres` — all genres A-Z, `Unknown` last, each with its album
   * count (TASK 4.3). Same shape as {@link ApiClient.getArtists}.
   *
   * Caller handles: `VALIDATION` (a tampered cursor — treat as "start over").
   */
  getGenres(options?: PageOptions): Promise<GenreListResponse>;
  /**
   * `GET /api/genres/:id` — one genre's albums, artist-then-year, no-year LAST
   * (TASK 4.3). Same shape as {@link ApiClient.getArtistAlbums}.
   *
   * Caller handles: `NOT_FOUND` (no such genre — a path back, NO Retry) and
   * `VALIDATION` (bad cursor).
   */
  getGenreAlbums(genreId: string, options?: PageOptions): Promise<GenreAlbumsResponse>;
  /**
   * `GET /api/admin/users` — the allowlist, offset-paged (TASK 2.4). Admin-only
   * server-side (`requireAdmin`); a member gets 403.
   */
  listAdminUsers(options?: AdminUsersPageOptions): Promise<AdminUserList>;
  /** `POST /api/admin/users` — add or reactivate by email. Caller handles: `VALIDATION`. */
  addAdminUser(email: string): Promise<AddAdminUserResult>;
  /** `POST /api/admin/users/:id/role` — promote/demote. Caller handles: `NOT_FOUND`, `FORBIDDEN` (self). */
  setAdminUserRole(entryId: string, role: Role): Promise<AdminUserMutationResult>;
  /** `POST /api/admin/users/:id/remove` — revoke access, retain data. */
  removeAdminUser(entryId: string): Promise<AdminUserMutationResult>;
  /** `POST /api/admin/users/:id/reactivate` — re-enable a removed entry. */
  reactivateAdminUser(entryId: string): Promise<AdminUserMutationResult>;
  /**
   * `POST /api/admin/users/:id/delete-data` — typed-confirmation cascade purge.
   * `confirm` is forwarded verbatim; the server re-checks it (AC 2.9).
   */
  deleteAdminUserData(entryId: string, confirm: string): Promise<AdminUserMutationResult>;
  /** `GET /api/admin/sync` — the six-field status readout (TASK 3.5). Admin-only. */
  getSyncStatus(options?: { readonly signal?: AbortSignal }): Promise<SyncStatusResponse>;
  /**
   * `POST /api/admin/sync` — enqueue a sync run. `already_running` is a 200
   * OUTCOME (`{status:'already_running'}`), not an error — a run in flight is
   * reported, never a second run started.
   */
  triggerSync(): Promise<SyncTriggerResponse>;
  /**
   * `POST /api/telemetry` — forward a batch of client §11 events (TASK 11.1).
   * 202 accepted; a 413 (batch over the server cap) is handled HERE by splitting
   * the batch in half and retrying each — an over-sized offline backlog is never
   * lost to a single rejection. Diagnostics-only: callers must swallow failures.
   */
  postTelemetry(batch: { readonly events: readonly unknown[] }): Promise<void>;
  /** Caller handles: nothing — a failed sign-out still clears the client. */
  signOut(): Promise<void>;
  /** Caller handles: see `telemetry-client.ts#dispositionOf`. */
  sendTelemetry(batch: readonly unknown[]): Promise<void>;
  /**
   * Applies the GLOBAL 401/403 handling to a failure the caller observed on a
   * request it issued ITSELF.
   *
   * The audio engine's preflight is the one request that cannot go through
   * {@link ApiClient.request}: it needs the raw `Response` (status,
   * `X-Cold-Start`, `Retry-After`) and must not consume two bytes of audio as
   * JSON. Without this hook it would have to reimplement the three-way 403
   * discrimination described in the module header — the single most dangerous
   * thing in this file to fork, since getting it wrong destroys a legitimate
   * user's downloads. This runs the identical `/api/me` probe, the identical
   * purge and the identical navigation.
   *
   * Safe to call for any status; anything other than 401/403 is a no-op.
   */
  handleAuthFailure(status: number, path: string): Promise<void>;
  /** True once a revocation purge has run in this page lifetime. */
  readonly purged: boolean;
}

/* ── Offline PlayEvent seam (TASK 10.1 Step 6 / AC 13.7) ──────────────────
 *
 * A SETTER, mirroring `state-store.js#setTelemetryEmitter`, rather than a
 * direct import of `lib/offline-store.ts`. Two reasons, both structural:
 * importing the store here would make the api-client depend on IndexedDB (it is
 * the module every unit test in the repo already substitutes), and the store
 * imports `telemetry-client.ts`, which imports THIS file — a cycle. The
 * composition root installs the sink at boot; until it does, the branch below
 * is inert and behaviour is exactly what it was.
 */

/** What {@link setOfflinePlayEventSink} accepts. */
export interface OfflinePlayEventSink {
  /** Persists events that could not be delivered. Must not reject fatally. */
  park(events: readonly PlayEventInput[]): Promise<void>;
}

let offlinePlayEventSink: OfflinePlayEventSink | null = null;

/**
 * Installs (or with `null`, removes) the offline PlayEvent sink.
 *
 * @returns the sink that was previously installed
 */
export function setOfflinePlayEventSink(
  sink: OfflinePlayEventSink | null,
): OfflinePlayEventSink | null {
  const previous = offlinePlayEventSink;
  offlinePlayEventSink = sink;
  return previous;
}

/**
 * `false` ONLY when the runtime is certain there is no network. A missing
 * `navigator` (node tests) reads as online, so nothing is parked by accident.
 */
function isOnlineNow(): boolean {
  return globalThis.navigator?.onLine !== false;
}

/**
 * Cursor-page query params. A `null` cursor is OMITTED rather than sent as the
 * string "null" — `cursorSchema` would 422 that, and "first page" is the
 * absence of the key (dto.ts `cursorPageQuerySchema`).
 */
function pageQuery(options: PageOptions): { query?: Record<string, string | number> } {
  const query: Record<string, string | number> = {};
  if (options.limit !== undefined) query.limit = options.limit;
  if (options.cursor !== undefined && options.cursor !== null) query.cursor = options.cursor;
  return Object.keys(query).length === 0 ? {} : { query };
}

export function createApiClient(deps: ApiClientDeps = {}): ApiClient {
  const {
    fetchImpl = (...args: Parameters<typeof fetch>) => globalThis.fetch(...args),
    readCookie = (name) =>
      typeof document === 'undefined' ? null : readCookieFrom(document.cookie, name),
    go = navigate,
    purge = purgeLocalState,
    newIdempotencyKey = () => globalThis.crypto.randomUUID(),
    baseUrl = '',
    catalogCache = createTtlCache({ namespace: 'sm.cat.' }),
  } = deps;

  /**
   * Serve a STATIC catalog read from the TTL cache, or fetch-and-store on a
   * miss (feature #4). Only safe for genre/artist data, which the static
   * library never changes between visits. A cache hit skips the network
   * entirely; the `signal` is irrelevant then and the auth handling in
   * `execute` runs on the next real fetch as always.
   *
   * @template T
   * @param key a stable string identifying the exact request (path + paging)
   * @param fetcher runs `execute(...)` on a miss
   */
  function cachedCatalog<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
    const hit = catalogCache.get<T>(key);
    if (hit !== null) return Promise.resolve(hit);
    return fetcher().then((value) => {
      catalogCache.set(key, value);
      return value;
    });
  }

  /** A stable cache key from an endpoint name + its paging options. */
  function catalogKey(name: string, options: PageOptions): string {
    return `${name}:l=${String(options.limit ?? '')}:c=${String(options.cursor ?? '')}`;
  }

  /** One purge per page lifetime; concurrent 403s must not wipe repeatedly. */
  let purged = false;
  /** De-duplicates concurrent `/api/me` probes triggered by parallel 403s. */
  let probeInFlight: Promise<ProbeResult> | null = null;
  const searchGuard = createLatestWins<SearchResponse>();

  type ProbeResult =
    | { readonly verdict: 'revoked' }
    | { readonly verdict: 'no-session' }
    | { readonly verdict: 'authorized'; readonly csrfToken: string }
    | { readonly verdict: 'inconclusive' };

  function buildUrl(path: string, query: RequestOptions<unknown>['query']): string {
    const url = `${baseUrl}${path}`;
    if (query === undefined) return url;
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) params.set(key, String(value));
    const qs = params.toString();
    return qs === '' ? url : `${url}?${qs}`;
  }

  /**
   * A GET to `/api/me` purely to make the server mint a `csrf_token` cookie.
   * The CSRF plugin issues it on ANY GET, before the authz gate runs, so this
   * works even for an unauthenticated caller. The response status is ignored.
   */
  async function mintCsrfCookie(): Promise<string | null> {
    try {
      await fetchImpl(buildUrl('/api/me', undefined), {
        method: 'GET',
        credentials: 'same-origin',
        headers: { accept: 'application/json' },
      });
    } catch {
      /* transport failure — the caller proceeds and will surface the real error */
    }
    return readCookie(CSRF_COOKIE_NAME);
  }

  /** The unambiguous allowlist read described in the module header. */
  async function probeAuthorization(): Promise<ProbeResult> {
    if (probeInFlight !== null) return probeInFlight;
    const run = (async (): Promise<ProbeResult> => {
      let response: Response;
      try {
        response = await fetchImpl(buildUrl('/api/me', undefined), {
          method: 'GET',
          credentials: 'same-origin',
          headers: { accept: 'application/json' },
        });
      } catch {
        // Cannot tell revoked from offline — never purge on a guess.
        return { verdict: 'inconclusive' };
      }
      if (response.status === 403) return { verdict: 'revoked' };
      if (response.status === 401) return { verdict: 'no-session' };
      if (!response.ok) return { verdict: 'inconclusive' };
      const parsed = meResponseSchema.safeParse(await readJson(response));
      if (!parsed.success) return { verdict: 'inconclusive' };
      return { verdict: 'authorized', csrfToken: parsed.data.csrfToken };
    })();
    probeInFlight = run;
    try {
      return await run;
    } finally {
      probeInFlight = null;
    }
  }

  /** AC 2.5's client half — runs at most once. */
  async function handleRevocation(): Promise<void> {
    if (purged) return;
    purged = true;
    // Best-effort by construction: purgeLocalState resolves even on failure.
    await purge();
    go('#/signin/denied');
  }

  /**
   * THE 401/403 decision, in one place.
   *
   * Extracted from `execute()` so the audio engine's raw preflight can reach
   * the identical logic (see {@link ApiClient.handleAuthFailure}) instead of
   * growing a second, divergent copy of the discrimination the module header
   * describes. `execute()` still owns the one thing that is request-shaped
   * rather than session-shaped: the bounded CSRF retry, which needs the probe's
   * token to compare against what it sent.
   *
   * @returns the probe result when the caller is still authorized (so
   *   `execute()` can decide about the CSRF retry), otherwise `null` — meaning
   *   the situation has already been handled and navigation has happened.
   */
  async function discriminateAuthFailure(
    status: number,
    path: string,
  ): Promise<ProbeResult | null> {
    if (status === 401) {
      go('#/signin');
      return null;
    }
    if (status !== 403) return null;
    // `/api/me` 403 is already the authoritative allowlist read.
    if (path === '/api/me') {
      await handleRevocation();
      return null;
    }
    const probe = await probeAuthorization();
    if (probe.verdict === 'revoked') {
      await handleRevocation();
      return null;
    }
    if (probe.verdict === 'no-session') {
      go('#/signin');
      return null;
    }
    return probe;
  }

  async function execute<T>(path: string, options: RequestOptions<T>, attempt: number): Promise<T> {
    const method = (options.method ?? 'GET').toUpperCase();
    const mutating = STATE_CHANGING.has(method);

    const headers: Record<string, string> = { accept: 'application/json' };
    if (options.body !== undefined) headers['content-type'] = 'application/json';

    let sentCsrf: string | null = null;
    if (mutating) {
      // Absent cookie ⇒ the request is GUARANTEED to 403; mint first instead.
      sentCsrf = readCookie(CSRF_COOKIE_NAME) ?? (await mintCsrfCookie());
      if (sentCsrf !== null) headers[CSRF_HEADER_NAME] = sentCsrf;
    }
    if (options.idempotent === true) headers[IDEMPOTENCY_HEADER_NAME] = newIdempotencyKey();

    let response: Response;
    try {
      response = await fetchImpl(buildUrl(path, options.query), {
        method,
        credentials: 'same-origin',
        headers,
        ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
        ...(options.signal === undefined ? {} : { signal: options.signal }),
        ...(options.keepalive === true ? { keepalive: true } : {}),
      });
    } catch (error) {
      const aborted =
        options.signal?.aborted === true || (error instanceof Error && error.name === 'AbortError');
      throw new NetworkError(aborted);
    }

    if (response.ok) {
      const data = await readJson(response);
      if (options.parse === undefined) return data as T;
      try {
        return options.parse(data);
      } catch {
        // A response that does not match the contract is a server-side defect;
        // the UI gets the generic message, never the validator's detail.
        throw new ApiError(ERROR_CODES.INTERNAL, response.status);
      }
    }

    const apiError = parseErrorBody(response.status, await readJson(response));

    if (options.raw === true) throw apiError;

    if (response.status === 401 || response.status === 403) {
      const probe = await discriminateAuthFailure(response.status, path);
      if (
        probe !== null &&
        probe.verdict === 'authorized' &&
        mutating &&
        attempt === 0 &&
        probe.csrfToken !== sentCsrf
      ) {
        // Stale double-submit token: the CSRF hook rejected BEFORE any handler
        // ran (`plugins/csrf.ts` — onRequest), so nothing mutated and exactly
        // one retry with the fresh token is safe. Bounded: attempt === 0 only.
        return execute(path, options, attempt + 1);
      }
      // Role denial (or a genuine CSRF failure we cannot fix): NO purge.
      throw apiError;
    }

    throw apiError;
  }

  /**
   * The recursive half of {@link ApiClient.postTelemetry}: POST one batch, and
   * on a 413 (server cap exceeded) SPLIT it and retry the halves rather than
   * dropping a backlog whole. `raw: true` for the same reason
   * {@link ApiClient.sendTelemetry} uses it — telemetry is diagnostic and a
   * 401/403 must not trigger the purge/navigation reflex.
   */
  async function postTelemetryBatch(events: readonly unknown[]): Promise<void> {
    if (events.length === 0) return;
    try {
      await execute('/api/telemetry', { method: 'POST', body: { events }, raw: true }, 0);
    } catch (error) {
      if (error instanceof ApiError && error.status === 413 && events.length > 1) {
        const mid = Math.floor(events.length / 2);
        await postTelemetryBatch(events.slice(0, mid));
        await postTelemetryBatch(events.slice(mid));
        return;
      }
      throw error;
    }
  }

  const client: ApiClient = {
    request<T>(path: string, options: RequestOptions<T> = {}): Promise<T> {
      return execute<T>(path, options, 0);
    },

    async getSession(): Promise<SessionResult> {
      try {
        const me = await execute(
          '/api/me',
          { parse: (data) => meResponseSchema.parse(data), raw: true },
          0,
        );
        return {
          status: 'authenticated',
          role: me.role,
          csrfToken: me.csrfToken,
          userId: me.userId,
        };
      } catch (error) {
        if (error instanceof ApiError) {
          if (error.status === 401) return { status: 'unauthenticated' };
          if (error.status === 403) {
            // Authenticated but off the allowlist: this IS the revocation case.
            await handleRevocation();
            return { status: 'unauthenticated' };
          }
        }
        // Transport failure / 5xx: the session is UNKNOWN. Callers must not
        // treat this as "signed out" and must not purge.
        return { status: 'unknown' };
      }
    },

    getHome(): Promise<HomeResponse> {
      return execute('/api/home', { parse: (data) => homeResponseSchema.parse(data) }, 0);
    },

    getRecentlyPlayedTracks(): Promise<RecentlyPlayedTracksResponse> {
      return execute(
        '/api/recently-played-tracks',
        { parse: (data) => recentlyPlayedTracksResponseSchema.parse(data) },
        0,
      );
    },

    search(query: string, options: SearchOptions = {}): Promise<LatestWinsOutcome<SearchResponse>> {
      // Params are added only when present — `cursor` in particular must be
      // ABSENT (not "undefined"/"null") to mean "first page", and the server
      // 422s a cursor that arrives without its `type` (see SearchOptions).
      const params: Record<string, string | number> = { q: query };
      if (options.limit !== undefined) params.limit = options.limit;
      if (options.cursor !== undefined && options.cursor !== null) params.cursor = options.cursor;
      if (options.type !== undefined) params.type = options.type;
      if (options.genreId !== undefined) params.genreId = options.genreId;
      return searchGuard.run((signal) =>
        execute(
          '/api/search',
          { query: params, signal, parse: (data) => searchResponseSchema.parse(data) },
          0,
        ),
      );
    },

    getArtists(options: PageOptions = {}): Promise<ArtistListResponse> {
      return cachedCatalog(catalogKey('artists', options), () =>
        execute(
          '/api/artists',
          {
            ...pageQuery(options),
            ...(options.signal === undefined ? {} : { signal: options.signal }),
            parse: (data) => artistListResponseSchema.parse(data),
          },
          0,
        ),
      );
    },

    getAlbums(options: PageOptions = {}): Promise<AlbumListResponse> {
      return cachedCatalog(catalogKey('albums', options), () =>
        execute(
          '/api/albums',
          {
            ...pageQuery(options),
            ...(options.signal === undefined ? {} : { signal: options.signal }),
            parse: (data) => albumListResponseSchema.parse(data),
          },
          0,
        ),
      );
    },

    getArtistAlbums(artistId: string, options: PageOptions = {}): Promise<ArtistAlbumsResponse> {
      return cachedCatalog(catalogKey(`artist:${artistId}`, options), () =>
        execute(
          `/api/artists/${encodeURIComponent(artistId)}`,
          {
            ...pageQuery(options),
            ...(options.signal === undefined ? {} : { signal: options.signal }),
            parse: (data) => artistAlbumsResponseSchema.parse(data),
          },
          0,
        ),
      );
    },

    getAlbum(
      albumId: string,
      options: { readonly signal?: AbortSignal } = {},
    ): Promise<AlbumDetailResponse> {
      return execute(
        `/api/albums/${encodeURIComponent(albumId)}`,
        {
          ...(options.signal === undefined ? {} : { signal: options.signal }),
          parse: (data) => albumDetailResponseSchema.parse(data),
        },
        0,
      );
    },

    getTrackLyrics(
      trackId: string,
      options: { readonly signal?: AbortSignal } = {},
    ): Promise<LyricsResponse> {
      // Plain read, no retry — same shape as getAlbum/getHome. The full player
      // owns its own per-track cache + generation guard (a track can change
      // faster than a fetch settles), so this stays a single clean GET.
      return execute(
        `/api/tracks/${encodeURIComponent(trackId)}/lyrics`,
        {
          ...(options.signal === undefined ? {} : { signal: options.signal }),
          parse: (data) => lyricsResponseSchema.parse(data),
        },
        0,
      );
    },

    addFavorite(input: AddFavoriteRequest): Promise<FavoriteResponse> {
      /*
       * THE KEY GOES IN THE BODY, NOT A HEADER.
       *
       * `addFavoriteRequestSchema` takes `idempotencyKey` as a BODY field.
       * Sending it only as the §8 `Idempotency-Key` header meant the server
       * never read it — the request was still idempotent by target, but the
       * key's actual purpose (a replay of the same key for a DIFFERENT target
       * being rejected as a 422 client bug rather than silently favouriting
       * something else) was inert. The header is still sent, because §8 makes
       * it the convention for add-type mutations and a future middleware may
       * key on it; the body field is what `apps/api` validates today.
       */
      const idempotencyKey = newIdempotencyKey();
      return execute(
        '/api/favorites',
        {
          method: 'POST',
          body: { ...input, idempotencyKey: input.idempotencyKey ?? idempotencyKey },
          // Add-type mutation (§8 API conventions) — a replayed key for the
          // SAME target is absorbed server-side; for a different one it 422s.
          idempotent: true,
          parse: (data) => favoriteResponseSchema.parse(data),
        },
        0,
      );
    },

    async removeFavorite(type: FavoriteTargetType, targetId: string): Promise<void> {
      await execute(
        `/api/favorites/${encodeURIComponent(type)}/${encodeURIComponent(targetId)}`,
        { method: 'DELETE' },
        0,
      );
    },

    listFavorites(options: FavoritesPageOptions = {}): Promise<FavoritesResponse> {
      const query: Record<string, string | number> = { ...pageQuery(options).query };
      if (options.type !== undefined) query.type = options.type;
      return execute(
        '/api/favorites',
        {
          ...(Object.keys(query).length === 0 ? {} : { query }),
          ...(options.signal === undefined ? {} : { signal: options.signal }),
          parse: (data) => favoritesResponseSchema.parse(data),
        },
        0,
      );
    },

    listPlaylists(options: PageOptions = {}): Promise<PlaylistListResponse> {
      return execute(
        '/api/playlists',
        {
          ...pageQuery(options),
          ...(options.signal === undefined ? {} : { signal: options.signal }),
          parse: (data) => playlistListResponseSchema.parse(data),
        },
        0,
      );
    },

    getPlaylist(playlistId: string, options: PageOptions = {}): Promise<PlaylistDetailResponse> {
      return execute(
        `/api/playlists/${encodeURIComponent(playlistId)}`,
        {
          ...pageQuery(options),
          ...(options.signal === undefined ? {} : { signal: options.signal }),
          parse: (data) => playlistDetailResponseSchema.parse(data),
        },
        0,
      );
    },

    createPlaylist(name: string): Promise<PlaylistSummary> {
      return execute(
        '/api/playlists',
        {
          method: 'POST',
          body: { name },
          // Add-type mutation: the §8 header is convention (the create body
          // schema has no key field — a duplicate replay lands as the 422
          // duplicate-name case instead, keyed `fieldErrors.name`).
          idempotent: true,
          parse: (data) => playlistSummarySchema.parse(data),
        },
        0,
      );
    },

    createPlaylistFromQueue(name: string): Promise<PlaylistSummary> {
      return execute(
        '/api/playlists/from-queue',
        {
          method: 'POST',
          body: { name },
          idempotent: true,
          parse: (data) => playlistSummarySchema.parse(data),
        },
        0,
      );
    },

    renamePlaylist(playlistId: string, name: string): Promise<PlaylistSummary> {
      return execute(
        `/api/playlists/${encodeURIComponent(playlistId)}`,
        { method: 'PATCH', body: { name }, parse: (data) => playlistSummarySchema.parse(data) },
        0,
      );
    },

    async deletePlaylist(playlistId: string): Promise<void> {
      await execute(`/api/playlists/${encodeURIComponent(playlistId)}`, { method: 'DELETE' }, 0);
    },

    addPlaylistTracks(
      playlistId: string,
      input: AddPlaylistTracksRequest,
    ): Promise<PlaylistSummary> {
      // THE KEY GOES IN THE BODY, NOT (ONLY) A HEADER — same contract as
      // addFavorite above: `addPlaylistTracksRequestSchema` validates the BODY
      // field; the header is the §8 convention. A caller-supplied key wins so
      // an offline outbox can replay, and "add anyway" after the duplicate
      // dialog is simply this call again with a FRESH key.
      const idempotencyKey = newIdempotencyKey();
      return execute(
        `/api/playlists/${encodeURIComponent(playlistId)}/tracks`,
        {
          method: 'POST',
          body: { ...input, idempotencyKey: input.idempotencyKey ?? idempotencyKey },
          idempotent: true,
          parse: (data) => playlistSummarySchema.parse(data),
        },
        0,
      );
    },

    async removePlaylistTrack(playlistId: string, itemId: string): Promise<void> {
      await execute(
        `/api/playlists/${encodeURIComponent(playlistId)}/tracks/${encodeURIComponent(itemId)}`,
        { method: 'DELETE' },
        0,
      );
    },

    reorderPlaylist(
      playlistId: string,
      itemId: string,
      move: MoveDirective,
    ): Promise<PlaylistSummary> {
      // POST but NOT add-type: a reorder repeated is the same order again, so
      // there is no idempotency key anywhere in its contract.
      return execute(
        `/api/playlists/${encodeURIComponent(playlistId)}/reorder`,
        {
          method: 'POST',
          body: { itemId, move },
          parse: (data) => playlistSummarySchema.parse(data),
        },
        0,
      );
    },

    getQueue(options: PageOptions = {}): Promise<QueueResponse> {
      return execute(
        '/api/queue',
        {
          ...pageQuery(options),
          ...(options.signal === undefined ? {} : { signal: options.signal }),
          parse: (data) => queueResponseSchema.parse(data),
        },
        0,
      );
    },

    reconcileQueue(
      options: {
        readonly after?: number;
        readonly limit?: number;
        readonly signal?: AbortSignal;
      } = {},
    ): Promise<QueueReconcileResponse> {
      // NUMERIC `after`, not pageQuery(): reconcile's position walk and the
      // display read's opaque cursors are different address spaces.
      const query: Record<string, string | number> = {};
      if (options.after !== undefined) query.after = options.after;
      if (options.limit !== undefined) query.limit = options.limit;
      return execute(
        '/api/queue/reconcile',
        {
          ...(Object.keys(query).length === 0 ? {} : { query }),
          ...(options.signal === undefined ? {} : { signal: options.signal }),
          parse: (data) => queueReconcileResponseSchema.parse(data),
        },
        0,
      );
    },

    mutateQueue(
      op: QueueOp,
      options: { readonly version?: string } = {},
    ): Promise<QueueMutationResponse> {
      // `add`/`playNext` are the add-type ops: mint a key into the OP BODY
      // (where `queueOpSchema` validates it) unless the caller replays its
      // own. `version` is spread conditionally — absent means "apply
      // unconditionally", and an explicit `undefined` is both a lie about that
      // and a strictObject/exactOptionalPropertyTypes violation.
      const isAdd = op.kind === 'add' || op.kind === 'playNext';
      const finalOp = isAdd
        ? { ...op, idempotencyKey: op.idempotencyKey ?? newIdempotencyKey() }
        : op;
      return execute(
        '/api/queue',
        {
          method: 'PUT',
          body: {
            ...(options.version === undefined ? {} : { version: options.version }),
            op: finalOp,
          },
          ...(isAdd ? { idempotent: true } : {}),
          parse: (data) => queueMutationResponseSchema.parse(data),
        },
        0,
      );
    },

    getQueueHistory(options: PageOptions = {}): Promise<QueueHistoryResponse> {
      return execute(
        '/api/queue/history',
        {
          ...pageQuery(options),
          ...(options.signal === undefined ? {} : { signal: options.signal }),
          parse: (data) => queueHistoryResponseSchema.parse(data),
        },
        0,
      );
    },

    recordQueueHistory(input: RecordQueueHistoryRequest): Promise<QueueHistoryEntry> {
      // 201 on first write; a replayed key returns the ORIGINAL entry — see
      // the interface JSDoc for why this is the most-retried request.
      return execute(
        '/api/queue/history',
        {
          method: 'POST',
          body: { ...input, idempotencyKey: input.idempotencyKey ?? newIdempotencyKey() },
          idempotent: true,
          parse: (data) => queueHistoryEntrySchema.parse(data),
        },
        0,
      );
    },

    readdQueueHistory(
      historyId: string,
      options: { readonly idempotencyKey?: string } = {},
    ): Promise<QueueMutationResponse> {
      return execute(
        `/api/queue/history/${encodeURIComponent(historyId)}/readd`,
        {
          method: 'POST',
          body: { idempotencyKey: options.idempotencyKey ?? newIdempotencyKey() },
          idempotent: true,
          parse: (data) => queueMutationResponseSchema.parse(data),
        },
        0,
      );
    },

    async sendPlayEvents(
      events: readonly PlayEventInput[],
      options: { readonly keepalive?: boolean } = {},
    ): Promise<PlayEventIngestResponse> {
      const totals = { received: 0, stored: 0, duplicates: 0, counted: 0 };
      if (events.length === 0) return totals;

      /*
       * AC 13.7 — OFFLINE CAPTURE. A play that happens in airplane mode must
       * still be counted once the device reconnects. The engine treats this
       * call as fire-and-forget diagnostics ("never surfaced, never retried
       * into a storm"), so without this branch an offline listen is simply
       * lost. Parking happens here, at the one place that knows the call
       * failed, and only for a TRANSPORT failure: a 4xx means the server
       * refused the batch on its merits and re-sending it later would be the
       * retry storm the engine deliberately avoids.
       */
      const sink = offlinePlayEventSink;
      if (sink !== null && !isOnlineNow()) {
        try {
          await sink.park(events);
        } catch {
          /* the sink is best-effort; never fail a send because parking did */
        }
        return totals;
      }
      // SPLIT rather than send-and-be-rejected: the endpoint answers 413 above
      // `PLAY_EVENT_BATCH_MAX`, and the client is the only place that knows the
      // batch is divisible. An unbounded array used to go straight to the wire.
      for (let i = 0; i < events.length; i += PLAY_EVENT_BATCH_MAX) {
        const chunk = events.slice(i, i + PLAY_EVENT_BATCH_MAX);
        try {
          const result = await execute(
            '/api/play-events',
            {
              method: 'POST',
              body: { events: chunk },
              ...(options.keepalive === true ? { keepalive: true } : {}),
              parse: (data) => playEventIngestResponseSchema.parse(data),
            },
            0,
          );
          totals.received += result.received;
          totals.stored += result.stored;
          totals.duplicates += result.duplicates;
          totals.counted += result.counted;
        } catch (error) {
          // TRANSPORT failure only — `navigator.onLine` lies about captive
          // portals and flaky radios, so the honest signal is "the request did
          // not reach a server". An ApiError DID reach one and is not parked.
          if (sink !== null && error instanceof NetworkError && !error.aborted) {
            await sink.park(chunk).catch(() => undefined);
            continue;
          }
          throw error;
        }
      }
      return totals;
    },

    async handleAuthFailure(status: number, path: string): Promise<void> {
      await discriminateAuthFailure(status, path);
    },

    getStats(
      options: { readonly period?: StatsPeriod; readonly signal?: AbortSignal } = {},
    ): Promise<StatsResponse> {
      // Query OMITTED when `period` is absent — the server defaults to all_time,
      // and `?period=undefined` would 422 the enum. Mirrors getHome: no retry.
      const query: Record<string, string> = {};
      if (options.period !== undefined) query.period = options.period;
      return execute(
        '/api/stats',
        {
          ...(Object.keys(query).length === 0 ? {} : { query }),
          ...(options.signal === undefined ? {} : { signal: options.signal }),
          parse: (data) => statsResponseSchema.parse(data),
        },
        0,
      );
    },

    getGenres(options: PageOptions = {}): Promise<GenreListResponse> {
      return cachedCatalog(catalogKey('genres', options), () =>
        execute(
          '/api/genres',
          {
            ...pageQuery(options),
            ...(options.signal === undefined ? {} : { signal: options.signal }),
            parse: (data) => genreListResponseSchema.parse(data),
          },
          0,
        ),
      );
    },

    getGenreAlbums(genreId: string, options: PageOptions = {}): Promise<GenreAlbumsResponse> {
      return cachedCatalog(catalogKey(`genre:${genreId}`, options), () =>
        execute(
          `/api/genres/${encodeURIComponent(genreId)}`,
          {
            ...pageQuery(options),
            ...(options.signal === undefined ? {} : { signal: options.signal }),
            parse: (data) => genreAlbumsResponseSchema.parse(data),
          },
          0,
        ),
      );
    },

    listAdminUsers(options: AdminUsersPageOptions = {}): Promise<AdminUserList> {
      const query: Record<string, number> = {};
      if (options.limit !== undefined) query.limit = options.limit;
      if (options.offset !== undefined) query.offset = options.offset;
      return execute(
        '/api/admin/users',
        {
          ...(Object.keys(query).length === 0 ? {} : { query }),
          ...(options.signal === undefined ? {} : { signal: options.signal }),
          parse: (data) => adminUserListSchema.parse(data),
        },
        0,
      );
    },

    addAdminUser(email: string): Promise<AddAdminUserResult> {
      // Add-type mutation: the §8 idempotency header is convention (the create
      // body schema has no key field — a duplicate lands as the `already_invited`
      // outcome instead). The view ignores the body, so it is not re-parsed.
      return execute<AddAdminUserResult>(
        '/api/admin/users',
        { method: 'POST', body: { email }, idempotent: true },
        0,
      );
    },

    setAdminUserRole(entryId: string, role: Role): Promise<AdminUserMutationResult> {
      return execute<AdminUserMutationResult>(
        `/api/admin/users/${encodeURIComponent(entryId)}/role`,
        { method: 'POST', body: { role } },
        0,
      );
    },

    removeAdminUser(entryId: string): Promise<AdminUserMutationResult> {
      return execute<AdminUserMutationResult>(
        `/api/admin/users/${encodeURIComponent(entryId)}/remove`,
        { method: 'POST' },
        0,
      );
    },

    reactivateAdminUser(entryId: string): Promise<AdminUserMutationResult> {
      return execute<AdminUserMutationResult>(
        `/api/admin/users/${encodeURIComponent(entryId)}/reactivate`,
        { method: 'POST' },
        0,
      );
    },

    deleteAdminUserData(entryId: string, confirm: string): Promise<AdminUserMutationResult> {
      return execute<AdminUserMutationResult>(
        `/api/admin/users/${encodeURIComponent(entryId)}/delete-data`,
        { method: 'POST', body: { confirm } },
        0,
      );
    },

    getSyncStatus(options: { readonly signal?: AbortSignal } = {}): Promise<SyncStatusResponse> {
      return execute(
        '/api/admin/sync',
        {
          ...(options.signal === undefined ? {} : { signal: options.signal }),
          parse: (data) => syncStatusResponseSchema.parse(data),
        },
        0,
      );
    },

    triggerSync(): Promise<SyncTriggerResponse> {
      // POST (CSRF applies). Both 202 (enqueued) and 200 (already_running) are
      // `response.ok`, so `already_running` returns normally — never an error.
      return execute(
        '/api/admin/sync',
        { method: 'POST', parse: (data) => syncTriggerResponseSchema.parse(data) },
        0,
      );
    },

    postTelemetry(batch: { readonly events: readonly unknown[] }): Promise<void> {
      return postTelemetryBatch(batch.events);
    },

    async signOut(): Promise<void> {
      await execute('/api/auth/signout', { method: 'POST', raw: true }, 0);
    },

    async sendTelemetry(batch: readonly unknown[]): Promise<void> {
      // The /api/telemetry route (TASK 11.1) validates an { events: [...] } envelope;
      // a bare array 422s. telemetry-client's flush owns retry/persist, so the simple
      // envelope here is correct — no 413-split needed at this layer.
      await execute('/api/telemetry', { method: 'POST', body: { events: batch }, raw: true }, 0);
    },

    get purged(): boolean {
      return purged;
    },
  };

  return client;
}

/**
 * The process-wide client. `modules/app/index.js#startApp` uses this one, so
 * every module here has a production call site.
 */
export const apiClient: ApiClient = createApiClient();
